'use client';
import { EmblaCarouselType } from 'embla-carousel';
import { classNames } from '@repo/ui/src/utils/classNames';
import React from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface SlideWrapperProps {
  children: React.ReactNode;
  index: number;
  length: number;
  className?: string;
  slideCount: number;
  selectedIndex: number;
  emblaApi: EmblaCarouselType | undefined;
}

const SlideWrapper = ({
  children,
  index,
  length,
  className,
  slideCount,
  selectedIndex,
  emblaApi,
}: SlideWrapperProps) => {
  const divRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(
    () => index >= selectedIndex && index < selectedIndex + Math.ceil(slideCount),
  );

  React.useEffect(() => {
    if (!emblaApi) return;
    const update = () => setIsVisible(emblaApi.slidesInView().includes(index));
    update();
    emblaApi.on('slidesInView', update);
    emblaApi.on('reInit', update);
    return () => {
      emblaApi.off('slidesInView', update);
      emblaApi.off('reInit', update);
    };
  }, [emblaApi, index]);

  // Prop-based fallback for when emblaApi is not yet initialised (e.g. SSR / first paint).
  React.useEffect(() => {
    if (emblaApi) return;
    setIsVisible(index >= selectedIndex && index < selectedIndex + Math.ceil(slideCount));
  }, [emblaApi, index, selectedIndex, slideCount]);

  const handleFocus = React.useCallback(() => {
    if (!emblaApi) return;
    if (!emblaApi.slidesInView().includes(index)) {
      emblaApi.scrollTo(index);
    }
  }, [emblaApi, index]);

  React.useEffect(() => {
    if (!divRef.current) return;
    const focusable = divRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable.forEach((el) => {
      if (isVisible) {
        if ('originalTabindex' in el.dataset) {
          if (el.dataset.originalTabindex === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', el.dataset.originalTabindex!);
          }
          delete el.dataset.originalTabindex;
        } else {
          el.removeAttribute('tabindex');
        }
      } else {
        if (!('originalTabindex' in el.dataset)) {
          el.dataset.originalTabindex = el.getAttribute('tabindex') ?? '';
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  }, [isVisible]);

  return (
    <div
      ref={divRef}
      role="group"
      aria-label={`slide ${index + 1} of ${length}`}
      aria-hidden={isVisible ? undefined : true}
      className={classNames('relative', className)}
      style={{ flex: `0 0 calc(100% / ${slideCount})` }}
      onFocus={handleFocus}
    >
      {children}
    </div>
  );
};

export default SlideWrapper;
